import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiUser, FiPlus, FiLogOut, FiBriefcase, FiMenu, FiX, FiSearch } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import './Header.css';

const DesktopHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Slideshow hidden feature state
  const logoClicksRef = useRef(0);
  const [showConstituencyModal, setShowConstituencyModal] = useState(false);
  const [allConstituencies, setAllConstituencies] = useState([]);
  const [constituencySearch, setConstituencySearch] = useState('');
  const [unreadLeadsCount, setUnreadLeadsCount] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const { api } = useContext(AuthContext);

  const filteredConstituencies = useMemo(() => {
    if (!constituencySearch.trim()) return allConstituencies;
    return allConstituencies.filter(c => c.toLowerCase().includes(constituencySearch.toLowerCase()));
  }, [allConstituencies, constituencySearch]);

  useEffect(() => {
    fetch('https://api.wealthassociate.in/discons/constituencys')
      .then(res => res.json())
      .then(data => {
        const unique = data.map(c => c.name).filter(Boolean).sort();
        setAllConstituencies(unique);
      })
      .catch(err => console.warn('Constituency fetch error:', err));
  }, []);

  useEffect(() => {
    if (user && ['seller', 'agent', 'builder', 'admin'].includes(user.role)) {
      api.get('/enquiries/unread-count')
        .then(res => {
          if (res.data && res.data.unreadCount !== undefined) {
            setUnreadLeadsCount(res.data.unreadCount);
          }
        })
        .catch(err => console.error('Failed to fetch unread count:', err));
    }
  }, [user, api]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogoClick = (e) => {
    e.preventDefault();
    logoClicksRef.current += 1;
    if (logoClicksRef.current >= 5) {
      setShowConstituencyModal(true);
      logoClicksRef.current = 0;
    } else {
      navigate('/');
    }
  };

  const handleSelectConstituency = (c) => {
    setShowConstituencyModal(false);
    navigate(`/slideshow?constituency=${encodeURIComponent(c)}`);
  };

  const isActive = (path) => {
    let match = false;
    
    if (path === '/properties') {
      match = location.pathname === '/properties' || 
              location.pathname.startsWith('/property/') || 
              location.pathname.startsWith('/wealth-property/') || 
              location.pathname === '/properties/featured' ||
              location.pathname === '/rent';
    } else if (path === '/projects') {
      match = location.pathname === '/projects' || 
              location.pathname.startsWith('/project/') ||
              location.pathname.startsWith('/wealth-project/');
    } else {
      match = location.pathname === path;
    }
    
    return match ? 'active font-bold text-primary' : 'font-semibold text-dark hover:text-primary transition-colors';
  };

  return (
    <header className="header bg-white py-4 shadow-sm relative z-[9999]">
      <div className="container flex items-center justify-between">
        <div className="flex items-center gap-4 md:gap-8">
          <div onClick={handleLogoClick} className="logo flex items-center gap-2 cursor-pointer">
            <img 
              src="/icon-192.png" 
              alt="Real Properties Icon" 
              style={{ width: '32px', height: '32px', objectFit: 'contain' }} 
            />
            <span className="font-bold text-lg md:text-xl whitespace-nowrap">Real Properties</span>
          </div>
          
            <nav className="nav hidden md:flex items-center gap-6">
              <Link to="/" className={isActive('/')}>Home</Link>
              <Link to="/map" className={isActive('/map')}>Map View</Link>
              <Link to="/properties" className={isActive('/properties')}>Properties</Link>
              <Link to="/projects" className={isActive('/projects')}>Projects</Link>
              <Link to="/page/about-us" className={isActive('/page/about-us')}>About Us</Link>
              <Link to="/contact-us" className={isActive('/contact-us')}>Contact Us</Link>
            </nav>
        </div>

        <div className="actions flex items-center gap-4 md:gap-6">
          {user ? (
            <div className="flex items-center gap-4">
              {(user.role === 'seller' || user.role === 'agent' || user.role === 'builder' || user.role === 'admin') && (
                <Link to={user.role === 'admin' ? '/admin?tab=add-property' : '/dashboard/add-property'} className="hidden md:flex btn btn-primary rounded-lg items-center gap-2 mr-2">
                  <FiPlus /> Add property
                </Link>
              )}
              {(user.role === 'builder' || user.role === 'admin') && (
                <Link to={user.role === 'admin' ? '/admin?tab=add-project' : '/dashboard/add-project'} className="hidden md:flex btn btn-primary rounded-lg items-center gap-2 mr-2">
                  <FiBriefcase /> Add project
                </Link>
              )}

              <div 
                className="relative hidden md:flex h-full items-center" 
                ref={dropdownRef}
              >
                <div 
                  className="relative text-dark hover:text-primary transition-colors text-xl p-2 cursor-pointer flex items-center gap-1" 
                  title="Profile"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <FiUser />
                  {unreadLeadsCount > 0 && (
                    <span className="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                      {unreadLeadsCount}
                    </span>
                  )}
                </div>
                
                {isDropdownOpen && (
                  <div className="absolute right-0 top-[110%] mt-2 w-48 bg-white rounded-lg shadow-xl border py-2 z-[10000] flex flex-col">
                    <Link onClick={() => setIsDropdownOpen(false)} to={user.role === 'admin' ? '/admin' : '/dashboard'} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Dashboard
                    </Link>
                    {['seller', 'agent', 'builder', 'admin'].includes(user.role) && (
                      <Link onClick={() => setIsDropdownOpen(false)} to={user.role === 'admin' ? '/admin?tab=manage-enquiries' : '/dashboard/manage-enquiries'} className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex justify-between items-center">
                        <span>Leads</span>
                        {unreadLeadsCount > 0 && (
                          <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">New ({unreadLeadsCount})</span>
                        )}
                      </Link>
                    )}
                    <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Link to="/login" className="hidden md:flex text-dark font-semibold hover:text-primary transition-colors items-center gap-1">
                <FiUser /> Sign in
              </Link>
            </div>
          )}
          
          <button 
            className="md:hidden text-2xl text-dark"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t absolute top-full left-0 w-full shadow-lg p-4 flex flex-col gap-4 z-50">
          <Link to="/" className={isActive('/')} onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
          <Link to="/map" className={isActive('/map')} onClick={() => setIsMobileMenuOpen(false)}>Map View</Link>
          <Link to="/properties" className={isActive('/properties')} onClick={() => setIsMobileMenuOpen(false)}>Properties</Link>
          <Link to="/projects" className={isActive('/projects')} onClick={() => setIsMobileMenuOpen(false)}>Projects</Link>
          <Link to="/page/about-us" className={isActive('/page/about-us')} onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
          <Link to="/contact-us" className={isActive('/contact-us')} onClick={() => setIsMobileMenuOpen(false)}>Contact Us</Link>
          
          <hr className="my-2" />
          
          {user ? (
            <div className="flex flex-col gap-4">
              <Link to={user.role === 'admin' ? '/admin' : '/dashboard'} className="font-semibold" onClick={() => setIsMobileMenuOpen(false)}>
                Dashboard
              </Link>
              {['seller', 'agent', 'builder', 'admin'].includes(user.role) && (
                <Link to={user.role === 'admin' ? '/admin?tab=manage-enquiries' : '/dashboard/manage-enquiries'} className="font-semibold flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                  Leads
                  {unreadLeadsCount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">New ({unreadLeadsCount})</span>
                  )}
                </Link>
              )}
              {(user.role === 'seller' || user.role === 'agent' || user.role === 'builder' || user.role === 'admin') && (
                <Link to={user.role === 'admin' ? '/admin?tab=add-property' : '/dashboard/add-property'} className="font-semibold text-primary" onClick={() => setIsMobileMenuOpen(false)}>
                  Add Property
                </Link>
              )}
              {(user.role === 'builder' || user.role === 'admin') && (
                <Link to={user.role === 'admin' ? '/admin?tab=add-project' : '/dashboard/add-project'} className="font-semibold text-primary" onClick={() => setIsMobileMenuOpen(false)}>
                  Add Project
                </Link>
              )}
              <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="text-left font-semibold text-red-500">
                Logout
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn btn-primary text-center" onClick={() => setIsMobileMenuOpen(false)}>
              Sign in
            </Link>
          )}
        </div>
      )}
      {/* Slideshow Constituency Modal */}
      {showConstituencyModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.8)', zIndex: 10000, display: 'flex',
          alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', padding: '24px', borderRadius: '12px',
            width: '90%', maxWidth: '400px', maxHeight: '80vh',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column'
          }}>
            <h2 style={{color: '#002f34', marginBottom: '16px', marginTop: 0, fontSize: '1.25rem', fontWeight: 'bold'}}>Select Constituency to View Slideshow</h2>
            
            <div style={{ position: 'relative', marginBottom: '16px', flexShrink: 0 }}>
              <FiSearch size={18} color="#727e80" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Search constituency..."
                value={constituencySearch}
                onChange={(e) => setConstituencySearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 40px',
                  borderRadius: '8px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  outline: 'none',
                  color: '#333'
                }}
              />
            </div>

            <div style={{ overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              <button 
                onClick={() => handleSelectConstituency('All')}
                style={{
                  padding: '12px 16px', background: '#002f34', color: '#fff',
                  border: 'none', borderRadius: '8px', textAlign: 'left',
                  cursor: 'pointer', fontWeight: 600, fontSize: '14px'
                }}
              >
                All Constituencies
              </button>
              {filteredConstituencies.map((c, i) => (
                <button 
                  key={i}
                  onClick={() => handleSelectConstituency(c)}
                  style={{
                    padding: '12px 16px', background: '#f5f7f7', color: '#002f34',
                    border: 'none', borderRadius: '8px', textAlign: 'left',
                    cursor: 'pointer', fontSize: '14px'
                  }}
                >
                  {c}
                </button>
              ))}
            </div>

            <button 
              onClick={() => setShowConstituencyModal(false)}
              style={{
                padding: '12px', background: '#fff', color: '#002f34',
                border: '1px solid #ccc', borderRadius: '8px',
                cursor: 'pointer', fontWeight: 600, fontSize: '14px'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default DesktopHeader;
